import { useCallback, useEffect } from 'react'

import { api } from '../api/client'

/**
 * The options for a passkey request, fetched **before** the tap -- so that
 * `navigator.credentials.get()` / `.create()` can run inside the gesture.
 *
 * From use, on the iPad: "Passkey funktioniert in der App, aber nicht im
 * Safari (am iPhone geht beides)." Safari demands a user gesture for a
 * WebAuthn call and lets exactly one call per page through without one. The
 * login page spends that one on the silent request for the address field.
 * The button then fetched its challenge from the server first and called the
 * browser afterwards -- outside the gesture, and Safari refused with
 * `NotAllowedError`, the same name it uses for a cancelled sheet. In the app
 * from the home screen there is no address field, so no silent request, and
 * the button was the first call. On the iPhone the passkey comes above the
 * keyboard, which is the silent request itself.
 *
 * So the challenge is on hand when the page opens; the tap hands it to the
 * browser without waiting for anything. One challenge serves both ways --
 * whichever completes consumes it on the server, the other is aborted.
 *
 * **Fresh and unused, or fetched anew.** The server keeps a challenge for
 * `Passkeys::CHALLENGE_TTL` (five minutes); after four the tap fetches a new
 * one and takes the slow way -- the rare case of a page left open. Once an
 * assertion has been sent to the server the challenge is gone, whatever the
 * server answered, and the next tap needs a new one.
 */
const FRESH_MS = 4 * 60 * 1000

interface Prepared<T> {
  promise: Promise<T>
  options: T | null
  at: number
  used: boolean
}

/**
 * **Kept per address, not per component instance.** Measured in the probe:
 * the login page is mounted twice on the way in, and an instance that
 * fetched its own challenge on mount had it still on its way when the tap
 * came a few hundred milliseconds later -- the slow way again. A store that
 * outlives the instance hands the second mount what the first one fetched.
 */
const store = new Map<string, Prepared<unknown>>()

function usable<T>(entry: Prepared<T> | undefined): entry is Prepared<T> {
  return entry !== undefined && !entry.used && Date.now() - entry.at < FRESH_MS
}

function prepare<T>(path: string): Prepared<T> {
  const entry: Prepared<T> = { promise: api.post<T>(path), options: null, at: Date.now(), used: false }
  entry.promise.then(
    (options) => {
      entry.options = options
    },
    () => {
      // A challenge that never arrived is not kept; the next tap asks again.
      if (store.get(path) === entry) store.delete(path)
    },
  )
  store.set(path, entry)
  return entry
}

export function usePreparedPasskeyOptions<T>(path: string, enabled: boolean) {
  const current = useCallback((): Prepared<T> | undefined => store.get(path) as Prepared<T> | undefined, [path])

  useEffect(() => {
    if (enabled && !usable(current())) prepare<T>(path)
  }, [enabled, path, current])

  return {
    /** The options if they are on hand right now -- for the call inside the gesture. */
    ready: (): T | null => {
      const entry = current()
      return usable(entry) && entry.options !== null ? entry.options : null
    },
    /** The options, fetched if need be -- for the silent request and the slow way. */
    promise: (): Promise<T> => {
      const entry = current()
      return (usable(entry) ? entry : prepare<T>(path)).promise
    },
    /** The challenge went to the server: whatever comes back, it is spent. */
    spend: (): void => {
      const entry = current()
      if (entry !== undefined) entry.used = true
      prepare<T>(path)
    },
  }
}
